/**
 * MomentumBar — Compact momentum signal strip
 *
 * Renders a slim bar that surfaces the most important momentum signals.
 * Context-aware: different behavior for list, detail, and dashboard pages.
 *
 * Visual (collapsed—default):
 *   ┌─────────────────────────────────────────────────────┐
 *   │ ⚡ 73  │ 🔴 2 blocked SGD$12k  │ ⚠️ 3  │  → [Fix]  │
 *   └─────────────────────────────────────────────────────┘
 *
 * Visual (expanded):
 *   ┌─────────────────────────────────────────────────────┐
 *   │ ⚡ 73                             [▲ Collapse]      │
 *   ├─────────────────────────────────────────────────────┤
 *   │ 🔴 3 jobs completed but not invoiced  SGD$8,500     │
 *   │    → Generate invoices now                          │
 *   │ ⚠️ 2 trips approaching SLA deadline (< 4h)         │
 *   │    → Open planner                                   │
 *   │ 🔴 AR overdue > 90 days  SGD$3,200                 │
 *   │    → Send collection notice                         │
 *   │ ✅ 15 jobs delivered on time today                  │
 *   └─────────────────────────────────────────────────────┘
 *
 * Works in: web-client TopBar area, go-pwa bottom card, extension-shell.
 */

import { useState, useCallback, type CSSProperties, type ReactNode } from 'react';
import type { MomentumSignal, MomentumScore, MomentumSeverity } from './types';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface MomentumBarProps {
  score: MomentumScore;
  signals: MomentumSignal[];
  topSignals: MomentumSignal[];
  hasCritical: boolean;
  blockedRevenue: number;
  blockedCurrency: string;
  enabled: boolean;
  onToggle: () => void;
  /** Handler for quick-action clicks */
  onAction?: (signal: MomentumSignal) => void;
  /** Surface type adjusts layout */
  variant?: 'desktop' | 'compact';
  /** Language */
  lang?: 'en' | 'zh';
  /** Extra class name */
  className?: string;
  /** Override styles */
  style?: CSSProperties;
}

// ─── Severity visual mapping ─────────────────────────────────────────────────

const SEVERITY_ICON: Record<MomentumSeverity, string> = {
  critical: '🔴',
  warning: '⚠️',
  info: 'ℹ️',
  positive: '✅',
};

const SEVERITY_BG: Record<MomentumSeverity, string> = {
  critical: '#fef2f2',
  warning: '#fffbeb',
  info: '#eff6ff',
  positive: '#f0fdf4',
};

const SEVERITY_BORDER: Record<MomentumSeverity, string> = {
  critical: '#fca5a5',
  warning: '#fcd34d',
  info: '#93c5fd',
  positive: '#86efac',
};

function formatMoney(amount: number, currency: string): string {
  if (amount >= 1_000_000) return `${currency}$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${currency}$${(amount / 1_000).toFixed(1)}k`;
  return `${currency}$${amount.toFixed(0)}`;
}

function scoreColor(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#ca8a04';
  if (score >= 40) return '#ea580c';
  return '#dc2626';
}

function trendSymbol(trend: 'up' | 'down' | 'stable'): string {
  return trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
}

// ─── Component ───────────────────────────────────────────────────────────────

export function MomentumBar(props: MomentumBarProps) {
  const {
    score,
    signals,
    topSignals,
    hasCritical,
    blockedRevenue,
    blockedCurrency,
    enabled,
    onToggle,
    onAction,
    variant = 'desktop',
    lang = 'en',
    className,
    style,
  } = props;

  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((v) => !v), []);

  if (!enabled || signals.length === 0) return null;

  const isCompact = variant === 'compact';

  // ─── Collapsed bar ──────────────────────────────────────────────────────

  const collapsedBar = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isCompact ? '8px' : '16px',
        padding: isCompact ? '6px 12px' : '6px 16px',
        fontSize: isCompact ? '12px' : '13px',
        lineHeight: '1.4',
        cursor: 'pointer',
        ...style,
      }}
      className={className}
      onClick={toggle}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label="Momentum signals bar"
    >
      {/* Score badge */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontWeight: 700,
          color: scoreColor(score.overall),
          whiteSpace: 'nowrap',
        }}
      >
        ⚡ {score.overall}
        <span style={{ fontSize: '10px', opacity: 0.7 }}>
          {trendSymbol(score.trend)}
        </span>
      </span>

      <span style={{ color: '#d1d5db' }}>│</span>

      {/* Critical count + blocked revenue */}
      {hasCritical && (
        <>
          <span style={{ color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
            🔴 {score.signalCounts.critical}
            {blockedRevenue > 0 && (
              <> {lang === 'zh' ? '阻塞' : 'blocked'} {formatMoney(blockedRevenue, blockedCurrency)}</>
            )}
          </span>
          <span style={{ color: '#d1d5db' }}>│</span>
        </>
      )}

      {/* Warning count */}
      {score.signalCounts.warning > 0 && (
        <>
          <span style={{ color: '#ca8a04', fontWeight: 500, whiteSpace: 'nowrap' }}>
            ⚠️ {score.signalCounts.warning}
          </span>
          <span style={{ color: '#d1d5db' }}>│</span>
        </>
      )}

      {/* Positive count */}
      {score.signalCounts.positive > 0 && (
        <span style={{ color: '#16a34a', whiteSpace: 'nowrap' }}>
          ✅ {score.signalCounts.positive}
        </span>
      )}

      {/* Top action shortcut */}
      {topSignals[0]?.suggestedAction && (
        <>
          <span style={{ color: '#d1d5db' }}>│</span>
          <button
            style={{
              background: 'none',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '12px',
              color: '#2563eb',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            onClick={(e) => {
              e.stopPropagation();
              const signal = topSignals[0];
              if (signal) onAction?.(signal);
            }}
          >
            → {lang === 'zh'
              ? topSignals[0].suggestedAction.labelZh
              : topSignals[0].suggestedAction.label}
          </button>
        </>
      )}

      {/* Expand hint */}
      <span style={{ marginLeft: 'auto', opacity: 0.5, fontSize: '11px' }}>
        {expanded ? '▲' : '▼'}
      </span>
    </div>
  );

  // ─── Expanded detail ────────────────────────────────────────────────────

  const MAX_VISIBLE = isCompact ? 5 : 8;
  const expandedPanel = expanded ? (
    <div
      style={{
        borderTop: '1px solid #e5e7eb',
        maxHeight: isCompact ? '220px' : '360px',
        overflowY: 'auto',
      }}
    >
      {/* Dimension summary row */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          padding: isCompact ? '6px 12px' : '6px 16px',
          borderBottom: '1px solid #f3f4f6',
          fontSize: '11px',
          color: '#6b7280',
        }}
      >
        <span>Revenue: <b style={{ color: scoreColor(score.dimensions.revenueReadiness) }}>{score.dimensions.revenueReadiness}</b></span>
        <span>Service: <b style={{ color: scoreColor(score.dimensions.serviceHealth) }}>{score.dimensions.serviceHealth}</b></span>
        <span>Cash: <b style={{ color: scoreColor(score.dimensions.cashVelocity) }}>{score.dimensions.cashVelocity}</b></span>
        <span>Growth: <b style={{ color: scoreColor(score.dimensions.growthSignal) }}>{score.dimensions.growthSignal}</b></span>
      </div>

      {/* Signal rows */}
      <div style={{ padding: isCompact ? '4px 12px' : '4px 16px' }}>
        {topSignals.slice(0, MAX_VISIBLE).map((signal) => (
          <SignalRow
            key={signal.id}
            signal={signal}
            lang={lang}
            onAction={onAction}
            compact={isCompact}
          />
        ))}
      </div>

      {signals.length > MAX_VISIBLE && (
        <div style={{
          textAlign: 'center',
          padding: '6px',
          fontSize: '11px',
          color: '#9ca3af',
          borderTop: '1px solid #f3f4f6',
        }}>
          {lang === 'zh'
            ? `还有 ${signals.length - MAX_VISIBLE} 条信号`
            : `${signals.length - MAX_VISIBLE} more signals`}
        </div>
      )}
    </div>
  ) : null;

  // ─── Container ──────────────────────────────────────────────────────────

  return (
    <div
      style={{
        background: hasCritical ? '#fef2f2' : '#fafafa',
        borderBottom: `1px solid ${hasCritical ? '#fca5a5' : '#e5e7eb'}`,
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      {collapsedBar}
      {expandedPanel}
    </div>
  );
}

// ─── SignalRow (internal) ────────────────────────────────────────────────────

function SignalRow(props: {
  signal: MomentumSignal;
  lang: 'en' | 'zh';
  onAction?: (signal: MomentumSignal) => void;
  compact?: boolean;
}) {
  const { signal, lang, onAction, compact } = props;
  const msg = lang === 'zh' ? signal.messageZh : signal.message;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: compact ? '4px 0' : '6px 0',
        borderBottom: '1px solid #f3f4f6',
        fontSize: compact ? '12px' : '13px',
      }}
    >
      <span style={{ flexShrink: 0 }}>{SEVERITY_ICON[signal.severity]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#374151' }}>
          {msg}
          {signal.monetaryImpact != null && signal.monetaryImpact > 0 && (
            <span style={{ marginLeft: '6px', fontWeight: 600, color: '#dc2626' }}>
              {formatMoney(signal.monetaryImpact, signal.currency ?? 'SGD')}
            </span>
          )}
          {signal.timeImpact != null && (
            <span style={{ marginLeft: '6px', color: '#9333ea', fontSize: '11px' }}>
              ({signal.timeImpact}{signal.timeUnit === 'hours' ? 'h' : 'd'})
            </span>
          )}
        </div>
        {signal.suggestedAction && (
          <button
            style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '4px',
              padding: '1px 8px',
              marginTop: '2px',
              fontSize: compact ? '11px' : '12px',
              color: '#2563eb',
              cursor: 'pointer',
              display: 'inline-block',
            }}
            onClick={() => onAction?.(signal)}
          >
            → {lang === 'zh'
              ? signal.suggestedAction!.labelZh
              : signal.suggestedAction!.label}
          </button>
        )}
      </div>
    </div>
  );
}
